import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Stack,
  Checkbox,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
} from '@mui/material';
import { ExpandMore, Settings, Visibility, VisibilityOff } from '@mui/icons-material';
import { propertyApi } from '../../services/BaseApiService.jsx';
import FieldStyleDialog from './FieldStyleDialog.jsx';

const cloneConfig = (value) => {
  if (!value) return null;
  try {
    return structuredClone ? structuredClone(value) : JSON.parse(JSON.stringify(value));
  } catch (_error) {
    console.warn('[ReportConfigAdmin] Falha ao clonar configuração, usando referência original.');
    return value;
  }
};

const DASHBOARD_CHIPS_SECTIONS = [
  {
    key: 'ambiental',
    label: 'Ambiental',
    chips: [
      { key: 'pra', mode: 'color' },
      { key: 'embargos', mode: 'color' },
    ],
  },
  {
    key: 'sobreposicoes',
    label: 'Sobreposições',
    chips: [
      { key: 'uc', mode: 'color' },
      { key: 'florestas', mode: 'color' },
      { key: 'aldeias', mode: 'color' },
      { key: 'quilombolas', mode: 'color' },
      { key: 'assentamentos', mode: 'color' },
      { key: 'terras_indigenas', mode: 'color' },
      { key: 'mineracao', mode: 'color' },
      { key: 'embargos_ibama', mode: 'color' },
      { key: 'pra', mode: 'color' },
    ],
  },
  {
    key: 'certidoes',
    label: 'Certidões',
    chips: [{ key: 'ibama', mode: 'color' }],
  },
  {
    key: 'areasRegistradas',
    label: 'Áreas Registradas',
    chips: [{ key: 'delta', mode: 'status' }],
  },
];

const DASHBOARD_COLOR_OPTIONS = ['default', 'success', 'warning', 'error'];

const DASHBOARD_STATUS_OPTIONS = ['success', 'warning', 'error'];

const DEFAULT_DASHBOARD_CHIPS = {
  ambiental: {
    pra: {
      thresholds: [
        { max: 0, color: 'success' },
        { min: 1, color: 'warning' },
      ],
    },
    embargos: {
      thresholds: [
        { max: 0, color: 'success' },
        { min: 1, color: 'warning' },
      ],
    },
  },
  sobreposicoes: {
    uc: {
      thresholds: [
        { max: 0, color: 'default' },
        { min: 1, color: 'warning' },
      ],
    },
    florestas: {
      thresholds: [
        { max: 0, color: 'default' },
        { min: 1, color: 'warning' },
      ],
    },
    aldeias: {
      thresholds: [
        { max: 0, color: 'default' },
        { min: 1, color: 'warning' },
      ],
    },
    quilombolas: {
      thresholds: [
        { max: 0, color: 'default' },
        { min: 1, color: 'warning' },
      ],
    },
    assentamentos: {
      thresholds: [
        { max: 0, color: 'default' },
        { min: 1, color: 'warning' },
      ],
    },
    terras_indigenas: {
      thresholds: [
        { max: 0, color: 'default' },
        { min: 1, color: 'warning' },
      ],
    },
    mineracao: {
      thresholds: [
        { max: 0, color: 'default' },
        { min: 1, color: 'warning' },
      ],
    },
    embargos_ibama: {
      thresholds: [
        { max: 0, color: 'default' },
        { min: 1, color: 'error' },
      ],
    },
    pra: {
      thresholds: [
        { max: 0, color: 'default' },
        { min: 1, color: 'warning' },
      ],
    },
  },
  certidoes: {
    ibama: {
      thresholds: [
        { max: 0, color: 'error' },
        { min: 1, max: 2, color: 'warning' },
        { min: 3, color: 'success' },
      ],
    },
  },
  areasRegistradas: {
    delta: {
      thresholds: [
        { max: -1, status: 'error' },
        { min: -1, max: 1, status: 'success' },
        { min: 1, status: 'warning' },
      ],
    },
  },
};

const mergeDashboardChipsWithDefaults = (chipsFromBackend) => {
  const base = cloneConfig(DEFAULT_DASHBOARD_CHIPS) || {};
  const current = chipsFromBackend && typeof chipsFromBackend === 'object' ? chipsFromBackend : {};

  const merged = { ...base };

  Object.keys(current).forEach((sectionKey) => {
    const section = current[sectionKey];
    if (!section || typeof section !== 'object') {
      merged[sectionKey] = section;
      return;
    }

    const targetSection = { ...(merged[sectionKey] || {}) };

    Object.keys(section).forEach((chipKey) => {
      const chipRules = section[chipKey];
      if (!chipRules || typeof chipRules !== 'object') {
        targetSection[chipKey] = chipRules;
        return;
      }

      const thresholds = Array.isArray(chipRules.thresholds) ? chipRules.thresholds : [];

      if (thresholds.length > 0) {
        targetSection[chipKey] = chipRules;
      }
    });

    merged[sectionKey] = targetSection;
  });

  return merged;
};

export default function ReportConfigAdmin() {
  const [config, setConfig] = useState(null);
  const [draftConfig, setDraftConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [styleDialogOpen, setStyleDialogOpen] = useState(false);
  const [currentFieldStyle, setCurrentFieldStyle] = useState(null);
  const [currentFieldKey, setCurrentFieldKey] = useState(null);
  const [_dashboardJsonText, setDashboardJsonText] = useState('');
  const [_dashboardJsonError, setDashboardJsonError] = useState(null);

  const activeConfig = isEditing ? draftConfig : config;

  useEffect(() => {
    let active = true;

    async function loadConfig() {
      setLoading(true);
      setError(null);
      try {
        const resp = await propertyApi.get('/report-config');
        if (!active) return;
        const data = resp?.data || null;
        if (data && typeof data === 'object') {
          setConfig(data);
          setDraftConfig(data);
          setIsEditing(false);
        } else {
          setError('Configuração inválida retornada pelo backend.');
        }
      } catch (_e) {
        if (!active) return;
        setError('Erro ao carregar configuração de relatório.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadConfig();

    return () => {
      active = false;
    };
  }, []);

  const domains = useMemo(() => {
    if (!activeConfig || !activeConfig.visibility) return [];
    return Object.keys(activeConfig.visibility);
  }, [activeConfig]);

  useEffect(() => {
    if (!selectedDomain && domains.length > 0) {
      setSelectedDomain(domains[0]);
    }
  }, [domains, selectedDomain]);

  const handleReload = async () => {
    setLoading(true);
    setError(null);
    setIsEditing(false);
    try {
      const resp = await propertyApi.get('/report-config');
      const data = resp?.data || null;
      if (data && typeof data === 'object') {
        setConfig(data);
        setDraftConfig(data);
      } else {
        setError('Configuração inválida retornada pelo backend.');
      }
    } catch (_e) {
      setError('Erro ao recarregar configuração de relatório.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = () => {
    if (!config) return;
    const cloned = cloneConfig(config);
    // Garantir que styles exista para poder adicionar estilos de campos
    if (!cloned.styles) {
      cloned.styles = {};
    }
    // Garantir que dashboard.chips exista para edição avançada
    if (!cloned.dashboard) {
      cloned.dashboard = {};
    }
    if (!cloned.dashboard.chips) {
      cloned.dashboard.chips = {};
    }
    const mergedChips = mergeDashboardChipsWithDefaults(cloned.dashboard.chips);
    cloned.dashboard.chips = mergedChips;
    setDraftConfig(cloned);
    setDashboardJsonText(JSON.stringify(mergedChips, null, 2));
    setDashboardJsonError(null);
    setIsEditing(true);
    setError(null);
  };

  const handleCancelEdit = () => {
    setDraftConfig(cloneConfig(config));
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!draftConfig) return;
    setSaving(true);
    setError(null);
    try {
      console.warn('[ReportConfigAdmin] Salvando configuração:', draftConfig);
      const resp = await propertyApi.put('/report-config', draftConfig);
      const data = resp?.data || draftConfig;
      console.warn('[ReportConfigAdmin] Resposta do backend:', data);
      setConfig(data);
      setDraftConfig(data);
      setIsEditing(false);
    } catch (e) {
      console.error('[ReportConfigAdmin] Erro ao salvar:', e);
      setError('Erro ao salvar configuração de relatório.');
    } finally {
      setSaving(false);
    }
  };

  const updateDraftConfig = (updater) => {
    if (!isEditing) return;
    setDraftConfig((prev) => {
      if (!prev) return prev;
      return updater(prev);
    });
  };

  // Auto-habilitar hierarquia superior
  const ensureHierarchyEnabled = (domainKey, sectionKey = null) => {
    updateDraftConfig((prev) => {
      const visibility = { ...(prev.visibility || {}) };
      const domainCfg = { ...(visibility[domainKey] || {}) };

      // Habilitar domínio se não estiver
      if (domainCfg.enabled === false) {
        domainCfg.enabled = true;
      }

      // Se foi informada seção, habilitar também
      if (sectionKey && domainCfg.sections) {
        const sections = { ...(domainCfg.sections || {}) };
        const sectionCfg = { ...(sections[sectionKey] || {}) };
        if (sectionCfg.enabled === false) {
          sectionCfg.enabled = true;
        }
        sections[sectionKey] = sectionCfg;
        domainCfg.sections = sections;
      }

      visibility[domainKey] = domainCfg;
      return { ...prev, visibility };
    });
  };

  const updateDomainVisibility = (domainKey, updater) => {
    updateDraftConfig((prev) => {
      const visibility = { ...(prev.visibility || {}) };
      const domainCfg = { ...(visibility[domainKey] || {}) };
      updater(domainCfg);
      visibility[domainKey] = domainCfg;
      return { ...prev, visibility };
    });
  };

  const updateSectionVisibility = (domainKey, sectionKey, updater) => {
    updateDomainVisibility(domainKey, (domainCfg) => {
      const sections = { ...(domainCfg.sections || {}) };
      const sectionCfg = { ...(sections[sectionKey] || {}) };
      updater(sectionCfg);
      sections[sectionKey] = sectionCfg;
      domainCfg.sections = sections;
    });
    // Auto-habilitar domínio ao alterar seção
    ensureHierarchyEnabled(domainKey);
  };

  const updateFieldVisibility = (domainKey, sectionKey, fieldKey, enabled) => {
    updateSectionVisibility(domainKey, sectionKey, (sectionCfg) => {
      const fields = { ...(sectionCfg.fields || {}) };
      const fieldCfg = { ...(fields[fieldKey] || {}) };
      fieldCfg.enabled = enabled;
      fields[fieldKey] = fieldCfg;
      sectionCfg.fields = fields;
    });
    // Auto-habilitar domínio e seção ao alterar campo
    ensureHierarchyEnabled(domainKey, sectionKey);
  };

  const updateSectionLabel = (domainKey, sectionKey, label) => {
    updateSectionVisibility(domainKey, sectionKey, (sectionCfg) => {
      sectionCfg.label = label;
    });
  };

  const updateFieldLabel = (domainKey, fieldKey, label) => {
    updateDraftConfig((prev) => {
      const labels = { ...(prev.labels || {}) };
      const domainLabels = { ...(labels[domainKey] || {}) };
      domainLabels[fieldKey] = label;
      labels[domainKey] = domainLabels;
      return { ...prev, labels };
    });
  };

  const handleOpenStyleDialog = (domainKey, sectionKey, fieldKey) => {
    const styles = activeConfig.styles || {};
    const domainStyles = styles[domainKey] || {};
    const fieldStyle = domainStyles[fieldKey] || {};

    setCurrentFieldKey(`${domainKey}.${fieldKey}`);
    setCurrentFieldStyle(fieldStyle);
    setStyleDialogOpen(true);
  };

  const handleSaveFieldStyle = (styleConfig) => {
    if (!currentFieldKey || !isEditing) return;

    const [domainKey, fieldKey] = currentFieldKey.split('.');

    updateDraftConfig((prev) => {
      const styles = { ...(prev.styles || {}) };
      const domainStyles = { ...(styles[domainKey] || {}) };
      domainStyles[fieldKey] = styleConfig;
      styles[domainKey] = domainStyles;
      return { ...prev, styles };
    });
  };

  const updateDashboardChip = (sectionKey, chipKey, nextRules) => {
    if (!isEditing) return;

    setDraftConfig((prev) => {
      if (!prev) return prev;

      const next = { ...prev };
      const dashboard = { ...(next.dashboard || {}) };
      const chips = { ...(dashboard.chips || {}) };
      const section = { ...(chips[sectionKey] || {}) };
      const updatedSection = { ...section, [chipKey]: nextRules || {} };
      const updatedChips = { ...chips, [sectionKey]: updatedSection };

      dashboard.chips = updatedChips;
      next.dashboard = dashboard;

      try {
        setDashboardJsonText(JSON.stringify(updatedChips, null, 2));
        setDashboardJsonError(null);
      } catch (_e) {
        // Se o stringify falhar por algum motivo, mantemos o texto atual.
      }

      return next;
    });
  };

  const ChipThresholdsEditor = ({
    sectionKey,
    chipKey,
    chipLabel,
    mode,
    rules,
    editable,
    onChange,
  }) => {
    const thresholds = Array.isArray(rules && rules.thresholds) ? rules.thresholds : [];

    const handleNumberChange = (index, field, rawValue) => {
      if (!editable) return;
      const value = typeof rawValue === 'string' ? rawValue.trim() : '';

      const nextThresholds = thresholds.map((t, i) => {
        if (i !== index) return t || {};
        const current = { ...(t || {}) };

        if (value === '') {
          delete current[field];
        } else {
          const parsed = Number(value.replace(',', '.'));
          if (Number.isFinite(parsed)) {
            current[field] = parsed;
          }
        }

        return current;
      });

      const nextRules = { ...(rules || {}), thresholds: nextThresholds };
      onChange(nextRules);
    };

    const handleEnumChange = (index, prop, value) => {
      if (!editable) return;

      const nextThresholds = thresholds.map((t, i) => {
        if (i !== index) return t || {};
        const current = { ...(t || {}) };

        if (!value) {
          delete current[prop];
        } else {
          current[prop] = value;
        }

        return current;
      });

      const nextRules = { ...(rules || {}), thresholds: nextThresholds };
      onChange(nextRules);
    };

    const options = mode === 'status' ? DASHBOARD_STATUS_OPTIONS : DASHBOARD_COLOR_OPTIONS;
    const selectProp = mode === 'status' ? 'status' : 'color';
    const selectLabel = mode === 'status' ? 'Status' : 'Cor';

    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            gap: 1,
          }}
        >
          <Typography variant="subtitle2">
            {chipLabel}{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              ({sectionKey}.{chipKey})
            </Typography>
          </Typography>
        </Box>

        {thresholds.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            Nenhuma faixa configurada para este chip.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {thresholds.map((t, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <TextField
                  label="Min"
                  size="small"
                  type="number"
                  value={typeof t?.min === 'number' ? t.min : ''}
                  disabled={!editable}
                  onChange={(e) => handleNumberChange(index, 'min', e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="Max"
                  size="small"
                  type="number"
                  value={typeof t?.max === 'number' ? t.max : ''}
                  disabled={!editable}
                  onChange={(e) => handleNumberChange(index, 'max', e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  label={selectLabel}
                  size="small"
                  select
                  value={t && typeof t[selectProp] === 'string' ? t[selectProp] : ''}
                  disabled={!editable}
                  onChange={(e) => handleEnumChange(index, selectProp, e.target.value)}
                  sx={{ width: 160 }}
                >
                  <MenuItem value="">
                    <em>Padrão</em>
                  </MenuItem>
                  {options.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const [expandedDashboardSections, setExpandedDashboardSections] = useState({});

  const renderDashboardChipsEditor = () => {
    const sourceConfig = isEditing ? draftConfig : activeConfig;
    if (!sourceConfig) return null;

    const dashboard = sourceConfig.dashboard || {};
    const chipsConfig = mergeDashboardChipsWithDefaults(dashboard.chips);

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Dashboard (configuração visual - thresholds)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Edite as faixas de <code>min</code>/<code>max</code> e a cor/status utilizada para cada
          chip do Dashboard.
        </Typography>

        {DASHBOARD_CHIPS_SECTIONS.map((section) => {
          const sectionChipsConfig = chipsConfig[section.key] || {};
          const key = section.key;
          const isExpanded = expandedDashboardSections[key] !== false;

          return (
            <Accordion
              key={section.key}
              expanded={isExpanded}
              onChange={() =>
                setExpandedDashboardSections((prev) => ({
                  ...prev,
                  [key]: !isExpanded,
                }))
              }
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2">{section.label}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {section.chips.map((chip) => {
                  const chipRules = sectionChipsConfig[chip.key] || {};

                  return (
                    <Box key={`${section.key}-${chip.key}`} sx={{ mb: 1.5 }}>
                      <ChipThresholdsEditor
                        sectionKey={section.key}
                        chipKey={chip.key}
                        chipLabel={chip.label || chip.key}
                        mode={chip.mode}
                        rules={chipRules}
                        editable={isEditing}
                        onChange={(nextRules) =>
                          updateDashboardChip(section.key, chip.key, nextRules)
                        }
                      />
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  const renderDomainDetails = () => {
    if (!activeConfig || !selectedDomain) return null;

    const visibility = activeConfig.visibility || {};
    const domainCfg = visibility[selectedDomain] || {};
    const sections = domainCfg.sections || {};
    const domainLabels = (activeConfig.labels && activeConfig.labels[selectedDomain]) || {};

    const sectionEntries = Object.entries(sections);

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Settings fontSize="small" color="action" />
          <Typography variant="h6">{selectedDomain}</Typography>
        </Box>

        {sectionEntries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Este domínio não possui seções configuradas.
          </Typography>
        ) : (
          <Box>
            {sectionEntries.map(([sectionKey, sectionCfg]) => {
              const sectionEnabled = sectionCfg.enabled !== false;
              const sectionLabel = sectionCfg.label || sectionKey;
              const fields = sectionCfg.fields || {};
              const fieldEntries = Object.entries(fields);
              const hasFields = fieldEntries.length > 0;
              const isExpanded = expandedDomains[`${selectedDomain}-${sectionKey}`] !== false;

              return (
                <Accordion
                  key={sectionKey}
                  expanded={hasFields && isExpanded}
                  onChange={() => {
                    if (hasFields) {
                      setExpandedDomains((prev) => ({
                        ...prev,
                        [`${selectedDomain}-${sectionKey}`]: !isExpanded,
                      }));
                    }
                  }}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary
                    expandIcon={hasFields ? <ExpandMore /> : null}
                    sx={{
                      bgcolor: sectionEnabled ? 'background.paper' : 'action.disabledBackground',
                      opacity: sectionEnabled ? 1 : 0.7,
                      '& .MuiAccordionSummary-content': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: '100%',
                        pr: hasFields ? 4 : 2,
                        gap: 2,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={sectionEnabled}
                        disabled={!isEditing}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          updateSectionVisibility(selectedDomain, sectionKey, (s) => {
                            s.enabled = e.target.checked;
                          })
                        }
                        size="small"
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                      >
                        {sectionKey}
                      </Typography>
                      {hasFields && (
                        <Typography variant="caption" color="text.secondary">
                          ({fieldEntries.length})
                        </Typography>
                      )}
                    </Box>

                    <TextField
                      label="Label"
                      size="small"
                      value={sectionLabel}
                      disabled={!isEditing}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        updateSectionLabel(selectedDomain, sectionKey, e.target.value)
                      }
                      sx={{
                        width: 180,
                        '& .MuiInputBase-root': { fontSize: '0.875rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                      }}
                    />
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0 }}>
                    {hasFields && (
                      <Box sx={{ width: '100%' }}>
                        {/* Cabeçalho da tabela */}
                        <Box
                          sx={{
                            display: 'flex',
                            bgcolor: 'grey.100',
                            px: 2,
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 150 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                            >
                              CAMPO
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 2, minWidth: 200 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                            >
                              LABEL
                            </Typography>
                          </Box>
                          <Box sx={{ width: 100, minWidth: 100 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                            >
                              CONFIGURAR
                            </Typography>
                          </Box>
                        </Box>

                        {/* Linhas da tabela */}
                        {fieldEntries.map(([fieldKey, fieldCfg]) => {
                          const fieldEnabled = fieldCfg.enabled !== false;
                          const fieldLabel = domainLabels[fieldKey] || fieldKey;

                          return (
                            <Box
                              key={fieldKey}
                              sx={{
                                display: 'flex',
                                px: 2,
                                py: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: fieldEnabled
                                  ? 'background.paper'
                                  : 'action.disabledBackground',
                                opacity: fieldEnabled ? 1 : 0.7,
                                '&:hover': {
                                  bgcolor: fieldEnabled ? 'grey.50' : 'action.hover',
                                },
                              }}
                            >
                              {/* Coluna Campo */}
                              <Box
                                sx={{
                                  flex: 1,
                                  minWidth: 150,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Checkbox
                                  checked={fieldEnabled}
                                  disabled={!isEditing}
                                  onChange={(e) =>
                                    updateFieldVisibility(
                                      selectedDomain,
                                      sectionKey,
                                      fieldKey,
                                      e.target.checked,
                                    )
                                  }
                                  size="small"
                                />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {fieldKey}
                                </Typography>
                              </Box>

                              {/* Coluna Label */}
                              <Box
                                sx={{
                                  flex: 2,
                                  minWidth: 200,
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <TextField
                                  label="Label"
                                  size="small"
                                  value={fieldLabel}
                                  disabled={!isEditing}
                                  onChange={(e) =>
                                    updateFieldLabel(selectedDomain, fieldKey, e.target.value)
                                  }
                                  sx={{
                                    width: '100%',
                                    '& .MuiInputBase-root': { fontSize: '0.8rem' },
                                    '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                                  }}
                                />
                              </Box>

                              {/* Coluna Configurar */}
                              <Box
                                sx={{
                                  width: 100,
                                  minWidth: 100,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                }}
                              >
                                <IconButton
                                  size="small"
                                  disabled={!isEditing}
                                  onClick={() =>
                                    handleOpenStyleDialog(selectedDomain, sectionKey, fieldKey)
                                  }
                                  sx={{
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                      bgcolor: 'primary.dark',
                                    },
                                    '&.Mui-disabled': {
                                      bgcolor: 'action.disabledBackground',
                                      color: 'action.disabled',
                                    },
                                  }}
                                >
                                  <Settings fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2, mt: { xs: 7, sm: 8 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5">Administração - Configuração de Relatório</Typography>
          <Typography variant="body2" color="text.secondary">
            Ajuste a visibilidade e os labels dos domínios, seções e campos do relatório.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" onClick={handleReload} disabled={loading || saving}>
            Recarregar
          </Button>
          <Button
            variant="contained"
            onClick={handleStartEdit}
            disabled={loading || !config || isEditing}
          >
            Editar
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancelEdit}
            disabled={!isEditing || saving}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isEditing || saving || !draftConfig}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && activeConfig && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: { md: '0 0 auto' }, minWidth: { md: 200 }, maxWidth: { md: 280 } }}>
              <Paper sx={{ p: 1 }} variant="outlined">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Domínios
                </Typography>
                <List dense>
                  {domains.map((domainKey) => {
                    const domainCfg = (activeConfig.visibility || {})[domainKey] || {};
                    const enabled = domainCfg.enabled !== false;
                    const isSelected = selectedDomain === domainKey;

                    return (
                      <ListItemButton
                        key={domainKey}
                        selected={isSelected}
                        onClick={() => setSelectedDomain(domainKey)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          border: isSelected ? '1px solid' : '1px solid transparent',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Checkbox
                            checked={enabled}
                            disabled={!isEditing}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              updateDomainVisibility(domainKey, (d) => {
                                d.enabled = e.target.checked;
                              })
                            }
                            size="small"
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: isSelected ? 600 : 400 }}
                                  >
                                    {domainKey}
                                  </Typography>
                                  {enabled ? (
                                    <Tooltip title="Domínio habilitado">
                                      <Visibility fontSize="small" color="success" />
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="Domínio desabilitado">
                                      <VisibilityOff fontSize="small" color="disabled" />
                                    </Tooltip>
                                  )}
                                </Box>
                              }
                              secondary={
                                <Typography
                                  variant="caption"
                                  color={enabled ? 'success.main' : 'text.secondary'}
                                  sx={{ fontWeight: 500 }}
                                >
                                  {enabled ? 'Habilitado' : 'Desabilitado'}
                                </Typography>
                              }
                            />
                          </Box>
                        </Box>
                      </ListItemButton>
                    );
                  })}

                  <ListItemButton
                    key="__dashboard__"
                    selected={selectedDomain === 'dashboard'}
                    onClick={() => setSelectedDomain('dashboard')}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      border:
                        selectedDomain === 'dashboard' ? '1px solid' : '1px solid transparent',
                      borderColor: selectedDomain === 'dashboard' ? 'primary.main' : 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: selectedDomain === 'dashboard' ? 600 : 400 }}
                              >
                                Dashboard
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 500 }}
                            >
                              Configuração de chips
                            </Typography>
                          }
                        />
                      </Box>
                    </Box>
                  </ListItemButton>
                </List>
              </Paper>
            </Box>
            <Box sx={{ flex: { md: '1 1 auto' } }}>
              {selectedDomain === 'dashboard' ? (
                renderDashboardChipsEditor()
              ) : selectedDomain ? (
                renderDomainDetails()
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Selecione um domínio para editar.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Dialog de configuração de estilo */}
      <FieldStyleDialog
        open={styleDialogOpen}
        onClose={() => setStyleDialogOpen(false)}
        onSave={handleSaveFieldStyle}
        fieldConfig={currentFieldStyle}
      />
    </Box>
  );
}
